import React, { useEffect, useState } from "react";
import {
  IconDownload,
  IconLoader2,
  IconShare,
  IconShare2,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { MButton } from "@/components/ui/atoms/MButtons";

interface MInstallGateScreenProps {
  canInstall: boolean;
  isIos: boolean;
  isMacSafari: boolean;
  onInstall: () => void;
  onCancel: () => void;
}

const Step: React.FC<{ number: number; text: string }> = ({ number, text }) => (
  <div className="flex items-start gap-3 text-left">
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-bold">
      {number}
    </span>
    <span className="text-sm text-white/90">{text}</span>
  </div>
);

const MInstallGateScreen: React.FC<MInstallGateScreenProps> = ({
  canInstall,
  isIos,
  isMacSafari,
  onInstall,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [showFallback, setShowFallback] = useState(false);

  // Show address-bar fallback hint if the browser hasn't fired beforeinstallprompt after 6s
  useEffect(() => {
    if (isIos || isMacSafari || canInstall) return;
    const timer = setTimeout(() => setShowFallback(true), 6000);
    return () => clearTimeout(timer);
  }, [isIos, isMacSafari, canInstall]);

  // Clear fallback once the prompt becomes available
  useEffect(() => {
    if (canInstall) setShowFallback(false);
  }, [canInstall]);

  const isManualInstall = isIos || isMacSafari;

  return (
    <div className="fixed inset-0 z-[1000] flex flex-col items-center justify-center gap-5 bg-[#481E9A] px-6 text-center text-white">
      <IconDownload size={40} />
      <p className="text-lg font-semibold">{t("common.installApp.title")}</p>

      {/* iOS Safari — numbered steps with share icon hint */}
      {isIos && (
        <>
          <p className="text-sm text-white/70">
            {t("common.installApp.iosDescription")}
          </p>
          <div className="flex w-full max-w-xs flex-col gap-3 rounded-xl bg-white/10 p-4">
            <Step number={1} text={t("common.installApp.iosStep1")} />
            <div className="flex justify-center">
              <IconShare size={28} className="text-white/60" />
            </div>
            <Step number={2} text={t("common.installApp.iosStep2")} />
            <Step number={3} text={t("common.installApp.iosStep3")} />
          </div>
        </>
      )}

      {/* macOS Safari — numbered steps */}
      {isMacSafari && !isIos && (
        <>
          <p className="text-sm text-white/70">
            {t("common.installApp.macSafariDescription")}
          </p>
          <div className="flex w-full max-w-xs flex-col gap-3 rounded-xl bg-white/10 p-4">
            <Step number={1} text={t("common.installApp.macSafariStep1")} />
            <div className="flex justify-center">
              <IconShare2 size={28} className="text-white/60" />
            </div>
            <Step number={2} text={t("common.installApp.macSafariStep2")} />
          </div>
        </>
      )}

      {/* Chromium browsers (Edge, Brave, Chrome) — native prompt */}
      {!isManualInstall && (
        <>
          <p className="text-sm text-white/80">
            {t("common.installApp.description")}
          </p>

          {!canInstall && !showFallback && (
            <IconLoader2 size={24} className="animate-spin text-white/60" />
          )}

          {canInstall && (
            <MButton
              variant="filled"
              size="md"
              text={t("common.install")}
              className="!bg-white !text-[#481E9A]"
              onClick={onInstall}
            />
          )}

          {showFallback && !canInstall && (
            <p className="max-w-xs text-xs text-white/60">
              {t("common.installApp.chromiumWaiting")}
            </p>
          )}
        </>
      )}

      <button
        type="button"
        onClick={onCancel}
        className="text-xs text-white/50 underline underline-offset-2"
      >
        {t("common.cancel")}
      </button>
    </div>
  );
};

export default MInstallGateScreen;
